export function randomHSB() {
  return {
    h: Math.floor(Math.random() * 360),
    s: 25 + Math.floor(Math.random() * 75),
    b: 25 + Math.floor(Math.random() * 75),
  }
}

/** First rounds use these hexes (in order), then random HSB */
export const CURATED_HEX_COLORS = [
  '#F29F00',
  '#291A02',
  '#E89900',
  '#AFAFB0',
  '#B49768',
  '#EDB693',
]

export function hexToHsb(hex) {
  const raw = String(hex).replace('#', '').trim()
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw
  const n = parseInt(full, 16)
  if (Number.isNaN(n) || full.length !== 6) return randomHSB()
  const r = ((n >> 16) & 255) / 255
  const g = ((n >> 8) & 255) / 255
  const b = (n & 255) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  let h = 0
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
    if (h < 0) h += 360
  }
  const bri = max
  const sat = max === 0 ? 0 : d / max
  return {
    h: Math.round(h),
    s: Math.round(sat * 100),
    b: Math.round(bri * 100),
  }
}

function randomHex() {
  // Avoid near-black / near-white so rounds stay guessable
  const r = 40 + Math.floor(Math.random() * 180)
  const g = 40 + Math.floor(Math.random() * 180)
  const b = 40 + Math.floor(Math.random() * 180)
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`
}

/**
 * First `curatedCount` rounds pick randomly from CURATED_HEX_COLORS,
 * remaining rounds fully random HSB.
 */
export function makeColorTargets(totalRounds, curatedCount = 3) {
  const n = Math.max(0, Math.min(totalRounds, curatedCount, CURATED_HEX_COLORS.length || totalRounds))
  const pool = [...CURATED_HEX_COLORS]
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[pool[i], pool[j]] = [pool[j], pool[i]]
  }
  const targets = []
  for (let i = 0; i < totalRounds; i++) {
    if (i < n) {
      const hex = pool[i] || randomHex()
      targets.push(hexToHsb(hex))
    } else {
      targets.push(randomHSB())
    }
  }
  return targets
}

export function hsbToCss({ h, s, b }) {
  const { r, g, bl } = hsbToRgb(h, s, b)
  return `rgb(${r}, ${g}, ${bl})`
}

export function hsbToRgb(h, s, b) {
  const sat = s / 100
  const bri = b / 100
  const c = bri * sat
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = bri - c
  let rp = 0
  let gp = 0
  let bp = 0
  if (h < 60) [rp, gp, bp] = [c, x, 0]
  else if (h < 120) [rp, gp, bp] = [x, c, 0]
  else if (h < 180) [rp, gp, bp] = [0, c, x]
  else if (h < 240) [rp, gp, bp] = [0, x, c]
  else if (h < 300) [rp, gp, bp] = [x, 0, c]
  else [rp, gp, bp] = [c, 0, x]
  return {
    r: Math.round((rp + m) * 255),
    g: Math.round((gp + m) * 255),
    bl: Math.round((bp + m) * 255),
  }
}

export function formatHSB({ h, s, b }) {
  return `H${Math.round(h)} S${Math.round(s)} B${Math.round(b)}`
}

export function scoreColor(target, guess) {
  // Hue is the main cue — ~75° error zeros that axis (was 180°)
  const dh =
    Math.min(Math.abs(target.h - guess.h), 360 - Math.abs(target.h - guess.h)) /
    75
  const ds = Math.abs(target.s - guess.s) / 65
  const db = Math.abs(target.b - guess.b) / 65
  const dist = Math.min(
    1,
    Math.sqrt(dh * dh * 2.1 + ds * ds * 1.05 + db * db) / Math.sqrt(4.15),
  )
  // Power curve so near-misses still drop more than a linear scale
  return Math.max(0, Math.min(100, 100 * Math.pow(1 - dist, 1.55)))
}

export function contrastText(hsb) {
  const { r, g, bl } = hsbToRgb(hsb.h, hsb.s, hsb.b)
  const luma = (0.299 * r + 0.587 * g + 0.114 * bl) / 255
  return luma > 0.55 ? '#111' : '#f5f5f5'
}
